import axios from 'axios';
import { API_URL, handleApiError } from '../utils/config';

const API = `${API_URL}/payments`;

// Helper function to handle Stripe-specific errors
const handleStripeError = (error) => {
  // Check if this is a Stripe error with additional details
  if (error.response && error.response.data && error.response.data.details) {
    const details = error.response.data.details;
    
    // Format a more helpful error message
    let errorMessage = `Payment error: ${error.response.data.error}`;
    
    if (details.code === 'resource_missing') {
      errorMessage = 'The requested Stripe resource was not found. This could be due to an expired session or invalid resource ID.';
    } else if (details.code) {
      errorMessage += ` (${details.code})`;
    }
    
    if (details.message) {
      errorMessage += `: ${details.message}`;
    }
    
    console.error('Stripe error details:', details);
    return errorMessage;
  }
  
  // Fall back to standard error handling
  return handleApiError(error);
};

// Helper function to get auth headers (optional for guest checkout)
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  
  // If no token is available, return empty headers for guest checkout
  if (!token) {
    return {};
  }
  
  // Return auth headers for logged-in users
  return {
    headers: {
      'x-auth-token': token,
      'Authorization': `Bearer ${token}` // Keep for backwards compatibility
    }
  };
};

const getGuestSessionPayload = () => {
  if (localStorage.getItem('token')) {
    return {};
  }

  const sessionId = localStorage.getItem('cart_session_id');
  return sessionId ? { sessionId } : {};
};

// Create a checkout session for an order
export const createCheckoutSession = async (orderId) => {
  try {
    const response = await axios.post(
      `${API}/checkout-session`,
      { orderId, ...getGuestSessionPayload() },
      getAuthHeaders()
    );
    
    // If we have a URL, redirect the user to the Stripe payment page
    if (response.data.url) {
      window.location.href = response.data.url;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    const errorMessage = handleStripeError(error);
    throw new Error(errorMessage);
  }
};

// Helper to extract Stripe session ID from URL if present
const getStripeSessionId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id') || urlParams.get('stripe_session');
  
  // If no session ID in URL params, check if it's in the path (Stripe redirects sometimes include it in the path)
  if (!sessionId) {
    // Extract from path if it matches a pattern like /success?session_id=cs_test_...
    const pathMatch = window.location.pathname.match(/\/success\/(cs_[a-zA-Z0-9_]+)/);
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }
    
    // Also check localStorage for a saved Stripe session ID
    const savedStripeSession = localStorage.getItem('stripe_session_id');
    if (savedStripeSession) {
      return savedStripeSession;
    }
  }
  
  // If we found a session ID, save it to localStorage for future use
  if (sessionId) {
    localStorage.setItem('stripe_session_id', sessionId);
  }
  
  return sessionId;
};

// Check payment status for an order (supports both authenticated users and guests)
export const checkPaymentStatus = async (orderId, directStripeSession = null) => {
  try {
    
    // For guest users, include the session ID as a query parameter
    let url = `${API}/check-status/${orderId}`;
    const headers = getAuthHeaders();
    const queryParams = [];
    
    // Use direct stripe session if provided, otherwise check URL and localStorage
    const stripeSessionId = directStripeSession || getStripeSessionId();
    if (stripeSessionId) {
      // Add Stripe session ID as a query parameter
      queryParams.push(`stripe_session=${stripeSessionId}`);
      
      // Also add it as a header for maximum compatibility
      headers.headers = headers.headers || {};
      headers.headers['x-stripe-session'] = stripeSessionId;
    }

    // Stripe proves payment state; the cart session still proves guest order
    // ownership and must be sent even on the Stripe success/cancel return URL.
    if (!localStorage.getItem('token')) {
      const sessionId = localStorage.getItem('cart_session_id');
      if (sessionId) {
        headers.headers = headers.headers || {};
        headers.headers['x-session-id'] = sessionId;
      }
    }
    
    // Add query parameters to URL if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await axios.get(url, headers);
    return response.data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    const errorMessage = handleStripeError(error);
    throw new Error(errorMessage);
  }
};

// Complete an order after payment, passing stripeSession for verification
export const completeOrder = async (orderId, stripeSession) => {
  try {
    // Include stripeSession in the request body for backend verification
    const requestBody = { stripe_session: stripeSession }; 
    const response = await axios.post(
      `${API}/complete-order/${orderId}`,
      requestBody, // Send stripeSession in the body
      getAuthHeaders()
    );
    
    // Clear cart in localStorage if we have any cart data there
    localStorage.removeItem('cart_items');
    
    // No need to dispatch cartUpdated event here as it should be done by the component
    // that calls this function (OrderSuccess.js or Checkout component)
    
    return response.data;
  } catch (error) {
    console.error('Error completing order:', error);
    const errorMessage = handleStripeError(error);
    throw new Error(errorMessage);
  }
};

// Retry a failed payment
export const retryPayment = async (orderId) => {
  try {
    const response = await axios.post(
      `${API}/retry-payment`,
      { orderId, ...getGuestSessionPayload() },
      getAuthHeaders()
    );
    
    // If we have a URL, redirect the user to the Stripe payment page
    if (response.data.url) {
      window.location.href = response.data.url;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error retrying payment:', error);
    const errorMessage = handleStripeError(error);
    throw new Error(errorMessage);
  }
};

// Cancel a payment
export const cancelPayment = async (orderId) => {
  try {
    
    // For guest users, include the session ID as a query parameter
    let url = `${API}/cancel-payment/${orderId}`;
    const headers = getAuthHeaders();
    const queryParams = [];
    
    // Check for Stripe session ID
    const stripeSessionId = getStripeSessionId();
    if (stripeSessionId) {
      // Add Stripe session ID as a query parameter
      queryParams.push(`stripe_session=${stripeSessionId}`);
      
      // Also add it as a header for maximum compatibility
      headers.headers = headers.headers || {};
      headers.headers['x-stripe-session'] = stripeSessionId;
      
    } 
    // If no Stripe session ID and this is a guest user, add the regular session ID
    else if (!localStorage.getItem('token')) {
      const sessionId = localStorage.getItem('cart_session_id');
      if (sessionId) {
        // Add session ID as both a query parameter and a header
        queryParams.push(`sessionId=${sessionId}`);
        headers.headers = headers.headers || {};
        headers.headers['x-session-id'] = sessionId;
      }
    }
    
    // Add query parameters to URL if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await axios.post(url, {}, headers);
    return response.data;
  } catch (error) {
    console.error('Error cancelling payment:', error);
    const errorMessage = handleStripeError(error);
    throw new Error(errorMessage);
  }
};
